import { useEffect, useRef, type ReactNode } from 'react'
import { ORGANIZATION_LABELS, type Organization } from '@/types/card'
import type { CardOfflineState } from '@/features/offline/storage'

/**
 * Kleine, wiederkehrende Bausteine.
 *
 * Grundsatz für alle: Bedienflächen sind mindestens 56 px hoch, Zustände werden nicht
 * allein über Farbe transportiert (Handschuhe, Sonnenlicht, Farbfehlsichtigkeit), und
 * nichts bewegt sich ohne Grund.
 */

/** Farbiger Kennstreifen der Organisation — dieselbe Logik wie ein Aktenreiter. */
export function OrgStripe({ organization }: { organization: Organization }) {
  return (
    <span
      aria-hidden
      className="absolute inset-y-0 left-0 w-1"
      style={{ background: `var(--org-${organization})` }}
    />
  )
}

export function OrgBadge({ organization }: { organization: Organization }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted">
      <span
        aria-hidden
        className="size-2 shrink-0"
        style={{ background: `var(--org-${organization})` }}
      />
      {ORGANIZATION_LABELS[organization]}
    </span>
  )
}

/**
 * Hülle für einen Hinweis, der eine Entscheidung verlangt.
 *
 * Als echter Dialog und nicht als Leiste am Rand: Eine geänderte Einsatzunterlage ist
 * keine Randnotiz. Der abgedunkelte Grund nimmt der Ansicht dahinter die Aufmerksamkeit,
 * und `showModal()` bringt mit, was ein Eigenbau erst nachbilden müsste — Fokus im
 * Dialog, der Rest der Seite unbedienbar, Escape schließt.
 *
 * Unten angedockt, nicht mittig: Im Einsatz wird einhändig bedient, und der Daumen
 * erreicht die untere Hälfte des Displays. Es gibt immer nur einen Dialog — welchen,
 * entscheidet `Hinweise.tsx`.
 */
export function Hinweisdialog({
  titel,
  onAbbruch,
  children,
}: {
  /** Was der Dialog will — für die Sprachausgabe, die ihn beim Öffnen ansagt. */
  titel: string
  /** Escape und die Zurück-Geste: dieselbe Wirkung wie die zurückhaltende Antwort. */
  onAbbruch: () => void
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const inhalt = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const dialog = ref.current
    dialog?.showModal()
    // showModal() setzt den Fokus auf das erste bedienbare Element — im
    // Änderungshinweis ist das ein Kartenname mitten im Satz, und der Fokusring stünde
    // dann als Kasten im Fließtext. Der Fokus gehört auf den Dialog selbst: Die
    // Sprachausgabe liest ihn von vorn, und keine Taste löst versehentlich etwas aus.
    inhalt.current?.focus()
    return () => dialog?.close()
  }, [])

  return (
    <dialog
      ref={ref}
      aria-label={titel}
      onCancel={(event) => {
        // Nicht den Dialog schließen und den Zustand danach nachziehen: Erst entscheidet
        // die App, dann verschwindet der Dialog. Sonst bliebe er beim nächsten Rendern
        // als geschlossenes Element stehen.
        event.preventDefault()
        onAbbruch()
      }}
      className="m-0 h-full max-h-none w-full max-w-none bg-transparent p-0
                 backdrop:bg-[rgb(0_0_0/0.6)]"
    >
      <div
        className="flex h-full items-end justify-center px-3"
        style={{ paddingBottom: 'calc(var(--nav-hoehe) + 0.75rem)' }}
      >
        <div
          ref={inhalt}
          tabIndex={-1}
          className="w-full max-w-2xl overflow-hidden rounded-card border border-line
                     bg-surface shadow-card focus:outline-none">
          {/* Akzentkante am Kopf: Sie macht auf einen Blick klar, dass hier etwas zu
              entscheiden ist — auch für den, der den Text noch nicht gelesen hat. */}
          <div aria-hidden className="h-1.5 bg-accent" />
          <div className="px-4 py-4">{children}</div>
        </div>
      </div>
    </dialog>
  )
}

/**
 * Marke für eine neue oder überarbeitete Karte.
 *
 * Als Wort, nicht als Farbpunkt: Die Aussage „diese Karte ist nicht mehr die, die du
 * kennst" muss auch bei Sonnenlicht und mit Handschuhen ankommen — und sie ist
 * wichtig genug, um den Platz zu rechtfertigen.
 */
export function UpdateMark({ kind }: { kind: 'neu' | 'aktualisiert' }) {
  return (
    <span className="inline-flex items-center rounded-chip border border-accent px-1.5 py-px
                     text-[11px] font-semibold uppercase tracking-wider text-accent">
      {kind === 'neu' ? 'Neu' : 'Aktualisiert'}
    </span>
  )
}

const OFFLINE_TEXT: Record<CardOfflineState, string> = {
  aktuell: 'Offline verfügbar',
  veraltet: 'Aktualisierung verfügbar',
  fehlt: 'Nur mit Netzverbindung',
}

/**
 * Offline-Zustand einer Karte. Form und Text unterscheiden sich, nicht nur die Farbe —
 * bei Sonnenlicht auf dem Display ist ein Farbunterschied oft nicht mehr zu erkennen.
 */
export function OfflineMark({ state, withText = false }: { state: CardOfflineState; withText?: boolean }) {
  const shape =
    state === 'aktuell'
      ? 'bg-[var(--ok)]'
      : state === 'veraltet'
        ? 'bg-[var(--accent)]'
        : 'border border-line-strong bg-transparent'

  return (
    <span className="inline-flex items-center gap-1.5" title={OFFLINE_TEXT[state]}>
      <span aria-hidden className={`size-2 shrink-0 ${shape}`} />
      <span className={withText ? 'text-xs text-muted' : 'sr-only'}>{OFFLINE_TEXT[state]}</span>
    </span>
  )
}

export function Label({ children }: { children: ReactNode }) {
  return <h2 className="label">{children}</h2>
}

/** Leerzustand — sagt, was fehlt und was jetzt zu tun ist. */
export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-line px-5 py-8 text-center">
      <p className="text-lg font-semibold text-ink-strong">{title}</p>
      {hint && <p className="mx-auto mt-1.5 max-w-[34ch] text-sm text-muted">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  )
}

type ButtonProps = {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'ghost' | 'danger'
  disabled?: boolean
  type?: 'button' | 'submit'
  full?: boolean
  'aria-label'?: string
  'aria-pressed'?: boolean
}

export function Button({
  children,
  onClick,
  variant = 'ghost',
  disabled,
  type = 'button',
  full,
  ...aria
}: ButtonProps) {
  const base =
    'tap inline-flex items-center justify-center gap-2 rounded-card px-4 text-sm font-semibold ' +
    'transition-[background-color,border-color,opacity] duration-100 ' +
    'disabled:opacity-40 disabled:pointer-events-none'

  const styles = {
    primary: 'bg-accent text-accent-ink hover:bg-accent-dim',
    ghost: 'border border-line-strong bg-surface text-ink hover:bg-raised',
    danger: 'border border-[var(--danger)] bg-transparent text-[var(--danger)] hover:bg-raised',
  }[variant]

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles} ${full ? 'w-full' : ''}`}
      {...aria}
    >
      {children}
    </button>
  )
}

/** Filterschalter. Der gewählte Zustand ist am Rahmen erkennbar, nicht nur an der Farbe. */
export function Chip({
  active,
  onClick,
  children,
  swatch,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
  swatch?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'inline-flex min-h-11 shrink-0 items-center gap-2 rounded-chip border px-3 text-sm font-semibold ' +
        'transition-colors duration-100 ' +
        (active
          ? 'border-accent bg-accent text-accent-ink'
          : 'border-line-strong bg-surface text-muted hover:text-ink')
      }
    >
      {swatch && <span aria-hidden className="size-2 shrink-0" style={{ background: swatch }} />}
      {children}
    </button>
  )
}

/** Hebt die Fundstellen im Trefferausschnitt hervor. */
export function Highlighted({
  text,
  ranges,
}: {
  text: string
  ranges: readonly (readonly [number, number])[]
}) {
  if (ranges.length === 0) return <>{text}</>

  const parts: ReactNode[] = []
  let cursor = 0
  for (const [start, end] of ranges) {
    if (start > cursor) parts.push(text.slice(cursor, start))
    parts.push(
      <mark key={start} className="bg-accent/25 font-semibold text-ink-strong">
        {text.slice(start, end)}
      </mark>,
    )
    cursor = end
  }
  if (cursor < text.length) parts.push(text.slice(cursor))
  return <>{parts}</>
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-muted" role="status">
      <span
        aria-hidden
        className="size-4 animate-spin border-2 border-line-strong border-t-accent"
      />
      {label}
    </div>
  )
}
